import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Swal from "sweetalert2";
import axios from "axios";
import StepProgress from "./components/StepProgress";
import { apibaseurl } from "../../environment";
import SnipperLoader from "../../core/common/loader/SnipperLoader";
import logo from "../../images/logo.png";
import { all_routes } from "../router/all_routes";

/* =========================================================================
   Shared helpers (module-level, no hooks, safe to reuse anywhere)
========================================================================= */

const getImageUrl = (file: File | null, serverImage?: string | null) => {
  if (file) return URL.createObjectURL(file);
  if (serverImage) return `${apibaseurl}${serverImage}`;
  return "";
};

const validateImage = (file: File | null) => {
  if (!file) {
    Swal.fire({
      icon: "warning",
      title: "Image Required",
      text: "Please select an image.",
    });
    return false;
  }

  const allowed = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

  if (!allowed.includes(file.type)) {
    Swal.fire({
      icon: "error",
      title: "Invalid File",
      text: "Only JPG, JPEG, PNG and WEBP images are allowed.",
    });
    return false;
  }

  const maxFileSize = 10 * 1024 * 1024; // 10 MB

  if (file.size > maxFileSize) {
    Swal.fire({
      icon: "error",
      title: "File Too Large",
      text: "Maximum file size is 10 MB.",
    });
    return false;
  }

  return true;
};

/* =========================================================================
   useCameraCapture — self-contained camera lifecycle.
   Each card that calls this gets its OWN videoRef/canvasRef/stream,
   so House and Location can never interfere with each other, and
   nothing here ever gets remounted by unrelated parent re-renders.
========================================================================= */

function useCameraCapture(labelForFileName: string) {
  const [showCamera, setShowCamera] = useState(false);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraError, setCameraError] = useState("");
  const [file, setFile] = useState<File | null>(null);

  const videoRef = useRef<HTMLVideoElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);

  const startCamera = async () => {
    setCameraError("");
    setCameraReady(false);

    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: {
          facingMode: { ideal: "environment" },
        },
        audio: false,
      });

      streamRef.current = stream;
      setShowCamera(true);
    } catch (error: any) {
      console.error(`[${labelForFileName}] Camera error:`, error);

      if (error?.name === "NotAllowedError") {
        setCameraError(
          "Camera permission was denied. Please allow camera access.",
        );
      } else if (error?.name === "NotFoundError") {
        setCameraError("No camera was found on this device.");
      } else {
        setCameraError("Unable to access the camera.");
      }
    }
  };

  const stopCamera = () => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((track) => track.stop());
      streamRef.current = null;
    }

    if (videoRef.current) {
      videoRef.current.pause();
      videoRef.current.srcObject = null;
    }

    setShowCamera(false);
    setCameraReady(false);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;

    if (!video || !canvas) return;

    if (!video.videoWidth || !video.videoHeight) {
      setCameraError("Camera is not ready yet. Please wait.");
      return;
    }

    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;

    const context = canvas.getContext("2d");
    if (!context) {
      setCameraError("Unable to capture photo.");
      return;
    }

    context.drawImage(video, 0, 0, canvas.width, canvas.height);

    canvas.toBlob(
      (blob) => {
        if (!blob) {
          setCameraError("Unable to create image.");
          return;
        }

        const capturedFile = new File(
          [blob],
          `${labelForFileName}_${Date.now()}.jpg`,
          {
            type: "image/jpeg",
          },
        );

        setFile(capturedFile);
        stopCamera();
      },
      "image/jpeg",
      0.9,
    );
  };

  useEffect(() => {
    if (!showCamera) return;

    const video = videoRef.current;
    const stream = streamRef.current;
    if (!video || !stream) return;

    video.srcObject = stream;

    const handleVideoReady = async () => {
      try {
        await video.play();
        if (video.videoWidth > 0 && video.videoHeight > 0) {
          setCameraReady(true);
        }
      } catch (error) {
        console.error("Video play error:", error);
        setCameraError("Unable to start camera preview.");
      }
    };

    if (video.readyState >= 2) {
      handleVideoReady();
    } else {
      video.onloadedmetadata = handleVideoReady;
    }

    return () => {
      video.onloadedmetadata = null;
    };
  }, [showCamera]);

  // stop the stream if the component unmounts while camera is open
  useEffect(() => {
    return () => {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((track) => track.stop());
      }
    };
  }, []);

  return {
    showCamera,
    cameraReady,
    cameraError,
    setCameraError,
    file,
    setFile,
    videoRef,
    canvasRef,
    startCamera,
    stopCamera,
    capturePhoto,
  };
}

/* =========================================================================
   HousePhotoCard / LocationPhotoCard
   Top-level components (declared outside DocumentUpload) so React
   never redefines them mid-render. Each owns its own camera state.
========================================================================= */

type CameraCardProps = {
  serverImage?: string | null;
  uploadApi: any; // must expose { callApi, loading }
  addressType?: string;
  onUploaded: () => Promise<void> | void;
};

export const HousePhotoCard: React.FC<CameraCardProps> = ({
  serverImage,
  uploadApi,
  addressType,
  onUploaded,
}) => {
  const cam = useCameraCapture("house");
  const preview = getImageUrl(cam.file, serverImage);

  const handleUpload = async () => {
    if (!validateImage(cam.file)) return;

    const formData = new FormData();
    formData.append("house_photo", cam.file!);
    formData.append("address_type", addressType ?? "");

    try {
      const res = await uploadApi.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "House photo uploaded successfully.",
        });
        cam.setFile(null);
        await onUploaded();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <div className="col-lg-6">
      <div
        className="h-100"
        style={{
          border: "1px solid #eceef3",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="fw-semibold mb-0">House Entry Gate Photo</h6>
            <span className="text-muted ">
              Stand in front of your house entry gate and take a photo.
            </span>
          </div>

          {serverImage && !cam.file && (
            <span className="badge bg-success">
              <i className="ti ti-check me-1"></i>
              Uploaded
            </span>
          )}
        </div>

        {cam.cameraError && (
          <div className="alert alert-danger py-2 text-center">
            {cam.cameraError}
          </div>
        )}

        {cam.showCamera ? (
          <>
            <div className="mb-3">
              <h6>Capture House Entry Gate Photo</h6>
              <small className="text-muted">
                Please stand in front of your house entry gate and take a photo.
              </small>
            </div>

            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                margin: "0 auto",
                background: "#000",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <video
                ref={cam.videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            </div>

            {cam.cameraError && (
              <div className="text-danger text-center mt-2">
                {cam.cameraError}
              </div>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={cam.capturePhoto}
                disabled={!cam.cameraReady}
              >
                <i className="ti ti-camera me-1"></i>
                {cam.cameraReady ? "Capture Photo" : "Starting Camera..."}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cam.stopCamera}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={cam.startCamera}
              >
                <i className="ti ti-camera me-2"></i>
                Open Camera
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={!cam.file || uploadApi.loading}
                onClick={handleUpload}
              >
                {uploadApi.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-upload me-1"></i>
                    Upload
                  </>
                )}
              </button>
            </div>

            <div
              style={{
                height: 200,
                width: 250,
                margin: "auto",
                border: "1px solid #ddd",
                borderRadius: "10px",
                background: "#f8f9fa",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="House Entry Gate Photo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div className="text-center text-muted">
                  <i className="ti ti-camera fs-1"></i>
                  <p className="mb-0">Capture House Entry Gate Photo</p>
                  <small>Camera capture required</small>
                </div>
              )}
            </div>
          </>
        )}

        <canvas ref={cam.canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
};

export const LocationPhotoCard: React.FC<CameraCardProps> = ({
  serverImage,
  uploadApi,
  addressType,
  onUploaded,
}) => {
  const cam = useCameraCapture("locality");
  const preview = getImageUrl(cam.file, serverImage);

  const handleUpload = async () => {
    if (!validateImage(cam.file)) return;

    const formData = new FormData();
    formData.append("locality_photo", cam.file!);
    formData.append("address_type", addressType ?? "");

    try {
      const res = await uploadApi.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "Location photo uploaded successfully.",
        });
        cam.setFile(null);
        await onUploaded();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  return (
    <div className="col-lg-6">
      <div
        className="h-100"
        style={{
          border: "1px solid #eceef3",
          borderRadius: "12px",
          padding: "16px",
        }}
      >
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h6 className="fw-semibold mb-0">House Building Photo</h6>
            <span className="text-muted ">
              Stand in front of your house building and take a photo.
            </span>
          </div>
          {serverImage && !cam.file && (
            <span className="badge bg-success">
              <i className="ti ti-check me-1"></i>
              Uploaded
            </span>
          )}
        </div>

        {cam.cameraError && (
          <div className="alert alert-danger py-2 text-center">
            {cam.cameraError}
          </div>
        )}
        {cam.showCamera ? (
          <>
            <div className="mb-3">
              <h6>Capture House Building Photo</h6>
              <small className="text-muted">
                Please stand in front of your house building and take a photo.
              </small>
            </div>

            <div
              style={{
                width: "100%",
                maxWidth: "500px",
                margin: "0 auto",
                background: "#000",
                borderRadius: "10px",
                overflow: "hidden",
              }}
            >
              <video
                ref={cam.videoRef}
                autoPlay
                playsInline
                muted
                style={{ width: "100%", display: "block" }}
              />
            </div>

            {cam.cameraError && (
              <div className="text-danger text-center mt-2">
                {cam.cameraError}
              </div>
            )}

            <div className="d-flex justify-content-center gap-2 mt-3">
              <button
                type="button"
                className="btn btn-primary"
                onClick={cam.capturePhoto}
                disabled={!cam.cameraReady}
              >
                <i className="ti ti-camera me-1"></i>
                {cam.cameraReady ? "Capture Photo" : "Starting Camera..."}
              </button>

              <button
                type="button"
                className="btn btn-outline-secondary"
                onClick={cam.stopCamera}
              >
                Cancel
              </button>
            </div>
          </>
        ) : (
          <>
            <div className="d-flex justify-content-between mb-3">
              <button
                type="button"
                className="btn btn-outline-primary"
                onClick={cam.startCamera}
              >
                <i className="ti ti-camera me-2"></i>
                Open Camera
              </button>

              <button
                type="button"
                className="btn btn-primary"
                disabled={!cam.file || uploadApi.loading}
                onClick={handleUpload}
              >
                {uploadApi.loading ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm me-2"
                      role="status"
                    />
                    Uploading...
                  </>
                ) : (
                  <>
                    <i className="ti ti-upload me-1"></i>
                    Upload
                  </>
                )}
              </button>
            </div>

            <div
              style={{
                height: 200,
                width: 250,
                margin: "auto",
                border: "1px solid #ddd",
                borderRadius: "10px",
                background: "#f8f9fa",
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                overflow: "hidden",
              }}
            >
              {preview ? (
                <img
                  src={preview}
                  alt="House Building Photo"
                  style={{
                    width: "100%",
                    height: "100%",
                    objectFit: "contain",
                  }}
                />
              ) : (
                <div className="text-center text-muted">
                  <i className="ti ti-camera fs-1"></i>
                  <p className="mb-0">Capture House Building Photo</p>
                  <small>Camera capture required</small>
                </div>
              )}
            </div>
          </>
        )}

        <canvas ref={cam.canvasRef} style={{ display: "none" }} />
      </div>
    </div>
  );
};

// A minimal authenticated request wrapper, preserving the old callApi/loading API.
function useAddressRequest(
  candidateId: string | null,
  token: string | undefined,
  method: "GET" | "PATCH",
  suffix = "",
) {
  const [loading, setLoading] = useState(false);
  const callApi = async (payload?: FormData | Record<string, unknown>) => {
    if (!candidateId || !token) {
      throw new Error("A valid verification link is required.");
    }
    setLoading(true);
    try {
      const endpoint = `${apibaseurl}/api/v1/candidates/addresses/${encodeURIComponent(candidateId)}${suffix ? `/${suffix}` : ""}`;
      const config = { headers: { Authorization: `Address ${token}` } };
      const response =
        method === "GET"
          ? await axios.get(endpoint, config)
          : await axios.patch(endpoint, payload ?? {}, config);
      return response.data;
    } finally {
      setLoading(false);
    }
  };
  return { callApi, loading };
}

/* =========================================================================
   DocumentUpload — parent page
========================================================================= */

const DocumentUpload = () => {
  const navigate = useNavigate();
  const { token } = useParams<{ token: string }>();
  const [candidateId, setCandidateId] = useState<string | null>(null);
  const [isTokenValid, setIsTokenValid] = useState<boolean | null>(null);
  const [tokenError, setTokenError] = useState("");
  const [validationLoading, setValidationLoading] = useState(true);

  // All address APIs use the candidate ID obtained from token validation.
  // Authorization is included in every candidate-specific request.
  const getAddressAPI = useAddressRequest(candidateId, token, "GET");
  const uploadIdAPI = useAddressRequest(
    candidateId,
    token,
    "PATCH",
    "id-proof-photo",
  );
  const uploadAddressAPI = useAddressRequest(
    candidateId,
    token,
    "PATCH",
    "address-proof-photo",
  );
  const uploadHouseAPI = useAddressRequest(
    candidateId,
    token,
    "PATCH",
    "house-photo",
  );
  const uploadLocationAPI = useAddressRequest(
    candidateId,
    token,
    "PATCH",
    "locality-photo",
  );
  const submitAddressApi = useAddressRequest(
    candidateId,
    token,
    "PATCH",
    "address-check-in-progress",
  );

  /* ---------------- States ---------------- */

  const [detailData, setDetailData] = useState<any>(null);
  const [confirmSubmission, setConfirmSubmission] = useState(true);

  // Only ID / Address use plain file-input upload now.
  // House / Location are handled entirely inside HousePhotoCard / LocationPhotoCard.
  const [files, setFiles] = useState({
    id: null as File | null,
    address: null as File | null,
  });

  useEffect(() => {
    let active = true;
    setValidationLoading(true);
    setIsTokenValid(null);
    setCandidateId(null);
    setTokenError("");
    setDetailData(null);
    setFiles({ id: null, address: null });

    const validateToken = async () => {
      if (!token) {
        if (!active) return;
        setIsTokenValid(false);
        setTokenError(
          "Verification token is missing. Please generate a new verification URL and try again.",
        );
        setValidationLoading(false);
        return;
      }
      try {
        const tokenRes = await axios.get(
          `${apibaseurl}/api/v1/candidates/addresses/validate-address-token/${encodeURIComponent(token)}`,
        );
        if (!active) return;
        const validation = tokenRes.data?.data;
        if (validation?.valid !== true || !validation?.candidateId) {
          setIsTokenValid(false);
          setTokenError(
            "This verification link is invalid or has expired. Please generate a new verification URL and try again.",
          );
          return;
        }
        setCandidateId(String(validation.candidateId));
        setIsTokenValid(true);
      } catch (error: any) {
        if (!active) return;
        setIsTokenValid(false);
        setTokenError(
          error?.response?.data?.message ||
            "This verification link is invalid or has expired. Please generate a new verification URL and try again.",
        );
      } finally {
        if (active) setValidationLoading(false);
      }
    };
    void validateToken();
    return () => {
      active = false;
    };
  }, [token]);

  /* ---------------- Fetch ---------------- */

  const fetchAddressDetails = async () => {
    if (!candidateId || isTokenValid !== true) return;
    try {
      const res = await getAddressAPI.callApi();

      if (res?.success) {
        setDetailData(res.data?.[0] ?? null);
      }
    } catch (err) {
      console.log(err);
    }
  };

  useEffect(() => {
    if (candidateId && isTokenValid === true) {
      void fetchAddressDetails();
    }
  }, [candidateId, isTokenValid]);

  /* ---------------- Select File ---------------- */

  const handleFileChange = (key: keyof typeof files, file: File | null) => {
    setFiles((prev) => ({
      ...prev,
      [key]: file,
    }));
  };

  /* ---------------- Upload (ID / Address) ---------------- */

  const uploadDocument = async ({
    file,
    api,
    field,
    fieldName,
  }: {
    file: File | null;
    api: any;
    field: keyof typeof files;
    fieldName: string;
  }) => {
    if (!candidateId || isTokenValid !== true || !detailData) return;
    if (!validateImage(file)) return;

    const formData = new FormData();

    formData.append(fieldName, file!);
    formData.append("address_type", detailData?.address_type ?? "");

    try {
      const res = await api.callApi(formData);

      if (res?.success) {
        Swal.fire({
          icon: "success",
          title: "Uploaded Successfully",
          text: res?.message ?? "Document uploaded successfully.",
        });

        setFiles((prev) => ({
          ...prev,
          [field]: null,
        }));

        await fetchAddressDetails();
      } else {
        Swal.fire({
          icon: "error",
          title: "Upload Failed",
          text: res?.message,
        });
      }
    } catch (err: any) {
      Swal.fire({
        icon: "error",
        title: "Upload Failed",
        text: err?.response?.data?.message ?? "Something went wrong",
      });
    }
  };

  /* ---------------- Submit ---------------- */

  const handleSubmit = async () => {
    if (!candidateId || isTokenValid !== true || !detailData) {
      await Swal.fire({
        icon: "error",
        title: "Verification Required",
        text: "Please use a valid verification link and load your address details before submitting.",
      });
      return;
    }
    if (
      !detailData?.id_proof_photo ||
      !detailData?.address_proof_photo ||
      !detailData?.house_photo ||
      !detailData?.locality_photo
    ) {
      Swal.fire({
        icon: "warning",
        title: "Documents Required",
        text: "Please upload all four documents.",
      });
      return;
    }

    if (!confirmSubmission) {
      Swal.fire({
        icon: "warning",
        title: "Confirmation Required",
        text: "Please accept the declaration.",
      });
      return;
    }

    const result = await Swal.fire({
      icon: "question",
      title: "Submit Address Verification?",
      text: "Are you sure you want to submit this address verification?",
      showCancelButton: true,
      confirmButtonText: "Yes, Submit",
      cancelButtonText: "Cancel",
    });

    if (!result.isConfirmed) return;

    try {
      const response = await submitAddressApi.callApi({});

      if (response?.success) {
        await Swal.fire({
          icon: "success",
          title: "Success",
          text:
            response?.message || "Address verification submitted successfully.",
        });
        navigate(all_routes.candidateVerificationSuccess);
      } else {
        Swal.fire({
          icon: "error",
          title: "Submission Failed",
          text: response?.message || "Something went wrong.",
        });
      }
    } catch (error: any) {
      Swal.fire({
        icon: "error",
        title: "Submission Failed",
        text:
          error?.response?.data?.message ||
          error?.message ||
          "Something went wrong.",
      });
    }
  };

  /* ---------------- Upload Card (ID / Address, unchanged) ---------------- */

  type UploadCardProps = {
    title: string;
    fileKey: keyof typeof files;
    fieldName: string;
    serverImage: string | null | undefined;
    uploadApi: any;
    accept?: string;
  };

  const UploadCard = ({
    title,
    fileKey,
    fieldName,
    serverImage,
    uploadApi,
    accept = "image/*",
  }: UploadCardProps) => {
    const preview = getImageUrl(files[fileKey], serverImage);

    return (
      <div className="col-lg-6">
        <div
          className="h-100"
          style={{
            border: "1px solid #eceef3",
            borderRadius: "12px",
            padding: "16px",
          }}
        >
          <div className="d-flex justify-content-between align-items-center mb-3">
            <h6 className="fw-semibold mb-0">
              {title}
              <span className="text-danger ms-1">*</span>
            </h6>

            {serverImage && (
              <span className="badge bg-success">
                <i className="ti ti-check me-1"></i>
                Uploaded
              </span>
            )}
          </div>

          <div className="d-flex gap-2 mb-3">
            <input
              key={files[fileKey] ? undefined : `${fileKey}-empty`}
              type="file"
              className="form-control"
              accept={accept}
              onChange={(e) =>
                handleFileChange(fileKey, e.target.files?.[0] || null)
              }
            />

            <button
              className="btn btn-primary flex-shrink-0"
              disabled={!files[fileKey] || uploadApi.loading}
              onClick={() =>
                uploadDocument({
                  file: files[fileKey],
                  api: uploadApi,
                  field: fileKey,
                  fieldName,
                })
              }
            >
              {uploadApi.loading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm me-2"
                    role="status"
                  />
                  Uploading...
                </>
              ) : (
                <>
                  <i className="ti ti-upload me-1"></i>
                  Upload
                </>
              )}
            </button>
          </div>

          <div
            style={{
              height: 200,
              width: 250,
              margin: "auto",
              border: "1px solid #ddd",
              borderRadius: "10px",
              background: "#f8f9fa",
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              overflow: "hidden",
            }}
          >
            {preview ? (
              <img
                src={preview}
                alt={title}
                style={{
                  width: "100%",
                  height: "100%",
                  objectFit: "contain",
                }}
              />
            ) : (
              <div className="text-center text-muted">
                <i className="ti ti-photo fs-1"></i>
                <p className="mb-0">No Image Selected</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };

  return (
    <>
      <header className="w-100 bg-white border-bottom">
        <div className="container-fluid px-3 px-md-4 px-lg-5">
          <div className="d-flex align-items-center justify-content-start">
            <img
              src={logo}
              alt="Company Logo"
              className="img-fluid"
              style={{ maxWidth: "140px", maxHeight: "55px" }}
            />
          </div>
        </div>
      </header>
      <div className="container mt-4">
        {validationLoading ||
        (isTokenValid === true && getAddressAPI.loading) ? (
          <SnipperLoader />
        ) : isTokenValid !== true ? (
          <div
            className="card border-danger shadow-sm my-4 mx-auto"
            style={{ maxWidth: 650 }}
          >
            <div className="card-body text-center p-5">
              <i
                className="ti ti-alert-circle text-danger"
                style={{ fontSize: 58 }}
              />
              <h4 className="text-danger fw-bold mt-3">
                Verification Link Invalid or Expired
              </h4>
              <p className="text-dark mb-2">
                {tokenError ||
                  "This verification link is invalid or has expired."}
              </p>
              <p className="text-secondary mb-0">
                Please generate a new verification URL and try again.
              </p>
            </div>
          </div>
        ) : (
          <>
            <StepProgress
              totalSteps={5}
              currentStep={5}
              completedSteps={[1, 2, 3, 4]}
            />
            <div className="card mb-4">
              <div className="card-header">
                <h5 className="mb-0 fw-semibold">Documents Upload</h5>
              </div>

              <div className="card-body pb-3">
                <div
                  style={{
                    backgroundColor: "#fdeaea",
                    border: "1px solid #f6cdd0",
                    borderRadius: "10px",
                    padding: "12px 16px",
                    textAlign: "center",
                  }}
                >
                  <p
                    className="fw-semibold text-center mb-0"
                    style={{ color: "#c0392b", fontSize: "13.5px" }}
                  >
                    Kindly upload an image in <strong>JPG, JPEG, PNG</strong>{" "}
                    format only. PDF and other file types are not allowed.
                    Maximum file size: <strong>10 MB</strong>.
                  </p>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="row g-4">
                  <UploadCard
                    title="ID Photo"
                    fileKey="id"
                    fieldName="id_proof_photo"
                    serverImage={detailData?.id_proof_photo}
                    uploadApi={uploadIdAPI}
                    accept=".jpg,.jpeg,.png,.webp,image/*"
                  />

                  <UploadCard
                    title="Address Proof"
                    fileKey="address"
                    fieldName="address_proof_photo"
                    serverImage={detailData?.address_proof_photo}
                    uploadApi={uploadAddressAPI}
                  />
                </div>

                <div className="mt-4 mb-3">
                  <div
                    className="d-flex align-items-center gap-2 px-3 py-2 mb-3 rounded-2"
                    style={{
                      background: "#FFF8F1",
                      border: "1px solid #FFD8A8",
                      borderLeft: "4px solid #E67E22",
                    }}
                  >
                    <i
                      className="ti ti-camera fs-3"
                      style={{ color: "#E67E22" }}
                    ></i>

                    <span className="text-dark">
                      <strong>Camera Capture Required:</strong> House and
                      Location photos must be captured using the{" "}
                      <strong>device camera</strong>. Existing photos cannot be
                      uploaded.
                    </span>
                  </div>
                </div>

                <div className="row g-4">
                  <HousePhotoCard
                    serverImage={detailData?.house_photo}
                    uploadApi={uploadHouseAPI}
                    addressType={detailData?.address_type}
                    onUploaded={fetchAddressDetails}
                  />

                  <LocationPhotoCard
                    serverImage={detailData?.locality_photo}
                    uploadApi={uploadLocationAPI}
                    addressType={detailData?.address_type}
                    onUploaded={fetchAddressDetails}
                  />
                </div>

                {/* Declaration */}

                <div
                  className="mt-4"
                  style={{
                    border: "1px solid #E8EBEF",
                    borderRadius: "12px",
                    background: "#FAFBFC",
                    padding: "18px 20px",
                  }}
                >
                  <div className="d-flex align-items-start">
                    <input
                      id="confirmSubmission"
                      type="checkbox"
                      className="form-check-input mt-1 me-3"
                      checked={confirmSubmission}
                      onChange={(e) => setConfirmSubmission(e.target.checked)}
                      style={{
                        width: "18px",
                        height: "18px",
                        cursor: "pointer",
                      }}
                    />

                    <div>
                      <h6 className="fw-semibold mb-2">
                        Declaration
                        <span className="text-danger ms-1">*</span>
                      </h6>

                      <label
                        htmlFor="confirmSubmission"
                        className="mb-0"
                        style={{
                          fontSize: "14px",
                          color: "#495057",
                          lineHeight: "1.7",
                          cursor: "pointer",
                        }}
                      >
                        I hereby declare that all the documents submitted are
                        authentic, belong to me, and have not been altered or
                        tampered with. I understand that providing false or
                        misleading information may result in rejection of my
                        verification/application.
                      </label>
                    </div>
                  </div>
                </div>

                {/* Buttons */}

                <div className="d-flex justify-content-end gap-3 mt-4">
                  <div className="d-flex gap-2 align-items-center mt-5">
                    <button
                      onClick={() => navigate(-1)}
                      className="btn btn-outline-primary"
                    >
                      Back
                    </button>
                    <button
                      className="btn btn-primary"
                      onClick={handleSubmit}
                      disabled={submitAddressApi.loading}
                    >
                      {submitAddressApi.loading ? "Submitting..." : "Submit"}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </>
  );
};

export default DocumentUpload;
